import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutWithChat from './LayoutWithChat';
import ResponsiveTable, { Column } from './ResponsiveTable';
import TabNav from './TabNav';
import { fetchTeamRosterDetailed, fetchTeamDetails, TeamPlayer, TeamRoster, Team as TeamDetails } from '../api/teams';

interface TeamProps {}

const Team: React.FC<TeamProps> = ( )=> {
  const navigate = useNavigate();
  const { leagueId, teamId } = useParams<{ leagueId: string; teamId: string }>();
  const [activeTab, setActiveTab] = useState<'roster' | 'analysis' | 'optimizer'>('roster');
  const [teamName, setTeamName] = useState<string>('');
  const [isOwnedByUser, setIsOwnedByUser] = useState<boolean>(false);
  const [roster, setRoster] = useState<TeamRoster | null>(null);
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Effect to fetch team data when component mounts
  useEffect(() => {
    if (leagueId && teamId) {
      loadTeamData();
    }
  }, [leagueId, teamId]);

  const loadTeamData = async () => {
    if (!leagueId || !teamId) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Fetch team details and roster in parallel
      const [teamDetailsResponse, rosterResponse] = await Promise.all([
        fetchTeamDetails(leagueId, teamId),
        fetchTeamRosterDetailed(leagueId, teamId)
      ]);
      
      setTeamDetails(teamDetailsResponse);
      setRoster(rosterResponse);
      setTeamName(teamDetailsResponse.name || rosterResponse.teamName || 'Team');
      setIsOwnedByUser(teamDetailsResponse.isOwnedByUser || false);
      
    } catch (err: any) {
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to load team data');
        console.error('Error loading team data:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'roster', label: 'Roster' },
    { id: 'analysis', label: 'Analysis' },
    ...(isOwnedByUser ? [{ id: 'optimizer', label: 'Lineup Optimizer' }] : [])
  ];

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePlayerClick = (playerName: string) => {
    navigate(`/player/${encodeURIComponent(playerName)}`);
  };

  // Position Strength Analysis columns configuration
  const positionStrengthColumns: Column[] = [
    {
      key: 'position',
      label: 'Position',
      width: '15%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'player',
      label: 'Top Player',
      width: '35%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'rank',
      label: 'Rank',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary text-center',
      render: (value) => `#${value}`
    },
    {
      key: 'avgPoints',
      label: 'Avg Points',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'grade',
      label: 'Grade',
      width: '20%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => (
        <div className="flex justify-end">
          <div className="w-6 font-mono">
            {value}
          </div>
        </div>
      )
    }
  ];

  // Weekly Performance columns configuration
  const weeklyPerformanceColumns: Column[] = [
    {
      key: 'week',
      label: 'Week',
      width: '20%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => `Week ${value}`
    },
    {
      key: 'score',
      label: 'Score',
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'projected',
      label: 'Projected',
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'opponent',
      label: 'Opponent',
      width: '25%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'result',
      label: 'Result',
      width: '15%',
      className: 'whitespace-nowrap text-sm font-medium',
      render: (value) => (
        <span className={value === 'W' ? 'text-green-400' : 'text-red-400'}>
          {value}
        </span>
      )
    }
  ];

  // Team Stats columns configuration
  const teamStatsColumns: Column[] = [
    {
      key: 'stat',
      label: '',
      width: '40%',
      className: 'whitespace-nowrap text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'value',
      label: '',
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'context',
      label: '',
      width: '40%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    }
  ];

  // Mock data for team analysis tables
  const positionStrengthData = [
    { position: 'QB', player: 'Patrick Mahomes', rank: 3, avgPoints: '22.5', grade: 'A' },
    { position: 'RB', player: 'Christian McCaffrey', rank: 1, avgPoints: '18.2', grade: 'A+' },
    { position: 'WR', player: 'Tyreek Hill', rank: 5, avgPoints: '20.1', grade: 'A-' },
    { position: 'TE', player: 'Travis Kelce', rank: 2, avgPoints: '14.3', grade: 'A' },
    { position: 'DST', player: 'Buffalo Bills', rank: 8, avgPoints: '8.5', grade: 'B+' }
  ];

  const weeklyPerformanceData = [
    { week: '8', score: '115.2', projected: '118.5', opponent: 'Gridiron Gladiators', result: 'L' },
    { week: '9', score: '125.8', projected: '121.3', opponent: 'Touchdown Titans', result: 'W' },
    { week: '10', score: '130.4', projected: '124.7', opponent: 'Victory Vipers', result: 'W' },
    { week: '11', score: '142.1', projected: '128.2', opponent: 'Sideline Strategists', result: 'W' }
  ];

  const teamStatsData = [
    { stat: 'Season High Score', value: '142.1 pts', context: 'Week 11 vs Sideline Strategists' },
    { stat: 'Season Low Score', value: '98.3 pts', context: 'Week 4 vs Endzone Enforcers' },
    { stat: 'Average Score', value: '123.3 pts', context: 'Per week (11 weeks)' },
    { stat: 'Points Above Projection', value: '+15.2 pts', context: 'Season total vs projections' },
    { stat: 'Best Position', value: 'RB', context: 'Christian McCaffrey (#1 overall)' }
  ];

  // Player columns configuration
  const playerColumns: Column[] = [
    {
      key: 'primaryPosition',
      label: 'POS',
      width: '15%',
      className: 'whitespace-nowrap text-sm font-semibold text-text-primary',
      render: (value) => value
    },
    {
      key: 'name',
      label: 'Player',
      width: '50%',
      className: 'whitespace-nowrap',
      render: (value, player) => (
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            handlePlayerClick(player.name);
          }}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
            {player.imageUrl ? (
              <img 
                src={player.imageUrl} 
                alt={player.name} 
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              getInitials(player.name)
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-text-primary">
              {player.name}
            </span>
            {player.injuryStatus && player.injuryStatus !== 'healthy' && (
              <span className={`ml-2 text-[10px] sm:text-[10px] font-medium ${
                player.injuryStatus === 'questionable' ? 'text-yellow-400' :
                player.injuryStatus === 'doubtful' ? 'text-orange-400' :
                player.injuryStatus === 'out' ? 'text-red-400' :
                'text-gray-400'
              }`}>
                {player.injuryStatus.toUpperCase()}
              </span>
            )}
            {player.onByeWeek && (
              <span className="ml-2 text-[10px] sm:text-[10px] font-medium text-purple-400">
                BYE
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'team',
      label: 'TEAM',
      width: '20%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'projectedPoints',
      label: 'PROJ',
      width: '15%',
      className: 'whitespace-nowrap text-sm font-medium text-text-secondary',
      render: (value, player) => {
        if (player.actualPoints !== undefined) {
          return player.actualPoints.toFixed(1);
        }
        return player.projectedPoints ? player.projectedPoints.toFixed(1) : '--';
      }
    }
  ];

  return (
    <LayoutWithChat>

            {/* Team Title */}
            <h1 className="text-4xl font-normal font-primary text-text-primary">
              {teamName}
            </h1>

            {/* Tab Navigation */}
            <TabNav
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId as 'roster' | 'analysis' | 'optimizer')}
            />

            {/* Roster Tab */}
            {activeTab === 'roster' && (
              <>
                {isLoading ? (
                  <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-text-secondary">Loading roster...</div>
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                    <div className="text-red-400">{error}</div>
                    <button
                      onClick={loadTeamData}
                      className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : roster ? (
                  <>
                    {/* Starters Table */}
                    {roster.starters && roster.starters.length > 0 && (
                      <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-normal font-primary text-text-primary">
                          Starters
                        </h2>
                        <ResponsiveTable
                          columns={playerColumns}
                          data={roster.starters}
                          keyExtractor={(player) => player.id}
                          onRowClick={(player) => handlePlayerClick(player.name)}
                          cardRenderer={(player) => (
                            <div
                              onClick={() => handlePlayerClick(player.name)}
                              className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="text-sm font-semibold text-text-primary">
                                    {player.primaryPosition}
                                  </div>
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
                                    {player.imageUrl ? (
                                      <img 
                                        src={player.imageUrl} 
                                        alt={player.name} 
                                        className="h-full w-full rounded-full object-cover"
                                      />
                                    ) : (
                                      getInitials(player.name)
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="text-base font-medium text-text-primary">
                                      {player.name}
                                    </h3>
                                    <div className="text-xs text-text-secondary">
                                      {player.team}
                                      {player.injuryStatus && player.injuryStatus !== 'healthy' && (
                                        <span className={`ml-2 text-[10px] font-medium ${
                                          player.injuryStatus === 'questionable' ? 'text-yellow-400' :
                                          player.injuryStatus === 'doubtful' ? 'text-orange-400' :
                                          player.injuryStatus === 'out' ? 'text-red-400' :
                                          'text-gray-400'
                                        }`}>
                                          {player.injuryStatus.toUpperCase()}
                                        </span>
                                      )}
                                      {player.onByeWeek && (
                                        <span className="ml-2 text-[10px] font-medium text-purple-400">
                                          BYE
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-text-primary">
                                    {player.actualPoints !== undefined ? 
                                      player.actualPoints.toFixed(1) : 
                                      player.projectedPoints ? player.projectedPoints.toFixed(1) : '--'
                                    }
                                  </div>
                                  <div className="text-xs text-text-secondary">
                                    {player.actualPoints !== undefined ? 'Actual' : 'Projected'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {/* Bench Table */}
                    {roster.bench && roster.bench.length > 0 && (
                      <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-normal font-primary text-text-primary">
                          Bench
                        </h2>
                        <ResponsiveTable
                          columns={playerColumns}
                          data={roster.bench}
                          keyExtractor={(player) => player.id}
                          onRowClick={(player) => handlePlayerClick(player.name)}
                          cardRenderer={(player) => (
                            <div
                              onClick={() => handlePlayerClick(player.name)}
                              className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="text-sm font-semibold text-text-primary">
                                    {player.primaryPosition}
                                  </div>
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
                                    {player.imageUrl ? (
                                      <img 
                                        src={player.imageUrl} 
                                        alt={player.name} 
                                        className="h-full w-full rounded-full object-cover"
                                      />
                                    ) : (
                                      getInitials(player.name)
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="text-base font-medium text-text-primary">
                                      {player.name}
                                    </h3>
                                    <div className="text-xs text-text-secondary">
                                      {player.team}
                                      {player.injuryStatus && player.injuryStatus !== 'healthy' && (
                                        <span className={`ml-2 text-[10px] font-medium ${
                                          player.injuryStatus === 'questionable' ? 'text-yellow-400' :
                                          player.injuryStatus === 'doubtful' ? 'text-orange-400' :
                                          player.injuryStatus === 'out' ? 'text-red-400' :
                                          'text-gray-400'
                                        }`}>
                                          {player.injuryStatus.toUpperCase()}
                                        </span>
                                      )}
                                      {player.onByeWeek && (
                                        <span className="ml-2 text-[10px] font-medium text-purple-400">
                                          BYE
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-text-primary">
                                    {player.actualPoints !== undefined ? 
                                      player.actualPoints.toFixed(1) : 
                                      player.projectedPoints ? player.projectedPoints.toFixed(1) : '--'
                                    }
                                  </div>
                                  <div className="text-xs text-text-secondary">
                                    {player.actualPoints !== undefined ? 'Actual' : 'Projected'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {/* Injured Reserve Table */}
                    {roster.injuredReserve && roster.injuredReserve.length > 0 && (
                      <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-normal font-primary text-text-primary">
                          Injured Reserve
                        </h2>
                        <ResponsiveTable
                          columns={playerColumns}
                          data={roster.injuredReserve}
                          keyExtractor={(player) => player.id}
                          onRowClick={(player) => handlePlayerClick(player.name)}
                          cardRenderer={(player) => (
                            <div
                              onClick={() => handlePlayerClick(player.name)}
                              className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors opacity-60"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="text-sm font-semibold text-text-primary">
                                    {player.primaryPosition}
                                  </div>
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
                                    {player.imageUrl ? (
                                      <img 
                                        src={player.imageUrl} 
                                        alt={player.name} 
                                        className="h-full w-full rounded-full object-cover"
                                      />
                                    ) : (
                                      getInitials(player.name)
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="text-base font-medium text-text-primary">
                                      {player.name}
                                    </h3>
                                    <div className="text-xs text-text-secondary">
                                      {player.team}
                                      <span className="ml-2 text-[10px] font-medium text-red-400">
                                        IR
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-text-secondary">
                                    --
                                  </div>
                                  <div className="text-xs text-text-secondary">
                                    Injured
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-text-secondary">No roster data available</div>
                  </div>
                )}
              </>
            )}

            {/* Analysis Tab */}
            {activeTab === 'analysis' && (
              <div className="flex flex-col gap-6">
                {/* Position Strength Analysis */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Position Strength Analysis
                  </h3>
                  <ResponsiveTable
                    columns={positionStrengthColumns}
                    data={positionStrengthData}
                    keyExtractor={(item) => item.position}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-text-secondary">
                              {item.position}
                            </div>
                            <h3 className="text-base font-medium text-text-primary">
                              {item.player}
                            </h3>
                          </div>
                          <div className="flex justify-end">
                            <div className="w-6 font-mono text-lg font-medium text-text-primary">
                              {item.grade}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-text-secondary">Rank:</span>
                          <span className="text-text-primary">#{item.rank}</span>
                          <span className="text-text-secondary">•</span>
                          <span className="text-text-secondary">Avg:</span>
                          <span className="text-text-primary">{item.avgPoints}</span>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Weekly Performance Trends */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Weekly Performance Trends
                  </h3>
                  <ResponsiveTable
                    columns={weeklyPerformanceColumns}
                    data={weeklyPerformanceData}
                    keyExtractor={(item) => item.week}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <h3 className="text-base font-medium text-text-primary mb-3">
                          Week {item.week}
                        </h3>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Score</div>
                            <div className="text-text-primary font-medium">{item.score}</div>
                          </div>
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Proj</div>
                            <div className="text-text-primary">{item.projected}</div>
                          </div>
                          <div>
                            <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Result</div>
                            <div className={`font-medium ${item.result === 'W' ? 'text-green-400' : 'text-red-400'}`}>
                              {item.result}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </div>

                {/* Team Stats Summary */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xl font-normal font-primary text-text-primary">
                    Team Stats
                  </h3>
                  <ResponsiveTable
                    columns={teamStatsColumns}
                    data={teamStatsData}
                    keyExtractor={(item) => item.stat}
                    cardRenderer={(item) => (
                      <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                        <h4 className="text-sm font-medium text-text-primary mb-1">
                          {item.stat}
                        </h4>
                        <div className="text-2xl font-bold text-text-primary mb-1">
                          {item.value}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {item.context}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Lineup Optimizer Tab */}
            {activeTab === 'optimizer' && isOwnedByUser && (
              <div className="flex flex-col gap-6">
                <h2 className="text-2xl font-normal font-primary text-text-primary">
                  Lineup Optimizer
                </h2>
                <div className="bg-bg-secondary rounded-lg p-6">
                  <p className="text-text-secondary">
                    Lineup optimizer coming soon! This will help you set the optimal lineup 
                    based on projections, matchups, and advanced analytics.
                  </p>
                </div>
              </div>
            )}
    </LayoutWithChat>
  );
};

export default Team;